import { Button, Dropdown, Spin, Tag } from 'antd';
import { ReloadOutlined, PlusOutlined, EllipsisOutlined, CloudUploadOutlined } from "@ant-design/icons";
import axios from 'axios';
import dayjs from 'dayjs';
import proj4 from 'proj4';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  RemoteImageAlert_GetList_ResponseItem,
  remoteRemoteImageAlert_RefreshLandsatWMS,
} from '~/common/typescript/apis/RemoteImageAlertAPIs';
import { API_URL_ADMIN, ENVIRONMENT } from '~/config/config';
import React from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { landsatItemsLEFTAtom, landsatItemsRIGHTAtom, landsatLoadingAtom } from './store';

proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs'); // Lat/Lng
proj4.defs(
  'EPSG:3857',
  '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs',
); // Web Mercator

export type LandsatItemProps = {
  item: RemoteImageAlert_GetList_ResponseItem;
};

const parseDateFromId = (rawId: string) => {
  const id = rawId.split('/').at(-1);
  if (!id) return '';
  // Tách chuỗi đầu tiên (trước dấu "_")
  const datetimePart = id.split('_')[0]; // "20241117T031011"
  if (datetimePart.length < 8) return new Date();
  
  // Tách ngày và giờ
  const datePart = datetimePart.slice(0, 8); // "20241117"
  const timePart = datetimePart.length >= 14 ? datetimePart.slice(9) : "000000"; 

  // Kết hợp lại thành định dạng ISO
  const isoString = `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}T${timePart.slice(0, 2)}:${timePart.slice(2, 4)}:${timePart.slice(4, 6)}Z`;

  // Tạo đối tượng Date
  return new Date(isoString);
};

const getBoundsFromItem = (item: any): [[number, number], [number, number]] | null => {
  if (!item) return null;
  const bbox = item.bbox;
  if (!bbox) return null;

  if (bbox.coordinates && Array.isArray(bbox.coordinates[0])) {
    const coords = bbox.coordinates[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    coords.forEach((pt: any) => {
      const x = Number(pt[0]);
      const y = Number(pt[1]);
      if (!isNaN(x) && !isNaN(y)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    });
    if (minX !== Infinity && maxX !== -Infinity) {
      return [[minX, minY], [maxX, maxY]];
    }
  }

  if (Array.isArray(bbox) && bbox.length === 4) {
    return [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
  }

  return null;
};

const LandsatItem = React.forwardRef((props: LandsatItemProps, ref) => {
  React.useImperativeHandle(ref, () => ({
    async refresh() {
      await doRefresh();
    },
    async viewOnQGIS() {
      await onClick_ViewOnQGIS();
    },
  }));

  // Add atoms to track items for left and right maps
  const [landsatItemsLeft, setLandsatItemsLeft] = useAtom(landsatItemsLEFTAtom);
  const [landsatItemsRight, setLandsatItemsRight] = useAtom(landsatItemsRIGHTAtom);
  const setLandsatLoading = useSetAtom(landsatLoadingAtom);

  const baseURL = API_URL_ADMIN;
  const [expired, setExpired] = useState(false);
  const [wms, setWms] = useState(props.item.wmsUrl);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const getItemName = () => {
    const shortId = props.item.id.split('_T').at(-1) || props.item.id.split('/').at(-1) || 'Unknown';
    return `Ảnh ${shortId} (${dayjs(parseDateFromId(props.item.id)).format('DD/MM/YYYY')}) `;
  };

  const fetchXYZ = async (wms: string) => {
    let url = wms;
    if (url.startsWith('/')) {
      url = `${API_URL_ADMIN}${url}`;
    }
    url = url.replace('{z}', '0');
    url = url.replace('{x}', '0');
    url = url.replace('{y}', '0');
    await axios
      .get(url)
      .then(() => {
        setExpired(false);
      })
      .catch(() => {
        setExpired(true);
      });
  };

  useEffect(() => {
    setWms(props.item.wmsUrl);
    fetchXYZ(props.item.wmsUrl);
  }, [props.item]);

  const doRefresh = async () => {
    if (!expired) {
      return;
    }
    setIsRefreshing(true);
    try {
      await remoteRemoteImageAlert_RefreshLandsatWMS(props.item.id).then((response) => {
        setExpired(false);
        setWms(response.wms);
        return 'success';
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const onClick_Refresh = () => {
    doRefresh();
  };

  const formData = new FormData();
  formData.append('url', wms);
  formData.append('name', getItemName());
  formData.append('command', 'ADD_REMOTE_IMAGE');

  const coordinates = props.item.bbox?.coordinates?.[0];
  if (coordinates && coordinates.length >= 3) {
    const xmin = coordinates[0][0];
    const xmax = coordinates[1][0];
    const ymin = coordinates[0][1];
    const ymax = coordinates[2][1];

    const min = proj4('EPSG:4326', 'EPSG:3857', [xmin, ymin]);
    const max = proj4('EPSG:4326', 'EPSG:3857', [xmax, ymax]);

    formData.append('bbox', `${min[0]},${min[1]},${max[0]},${max[1]}`);
  }

  const onClick_ViewOnQGIS = async () => {
    try {
      await axios.post(`http://localhost:13000`, formData);
      toast.success('Đã thêm lớp bản đồ lên QGIS', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } catch (error) {
      toast.error('Lỗi: Hãy thử bật QGIS và bật plugin TLGeo', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      console.log(error);
    }
  };

  // Function to add image to the left map
  const onClick_AddToLeftMap = async () => {
    // Check if the item already exists in the left map
    const exists = landsatItemsLeft.some(item => item.id === props.item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }

    setIsAdding(true);
    setLandsatLoading({
      isLoading: true,
      text: `Đang làm mới và chuẩn bị ${getItemName()} cho bản đồ trái...`,
    });

    try {
      // 1. Luôn gọi refresh WMS để lấy token mới nhất từ GEE và cập nhật DB
      const res = await remoteRemoteImageAlert_RefreshLandsatWMS(props.item.id);
      let activeWms = res?.wms || wms;
      if (res && res.wms) {
        activeWms = res.wms;
        setWms(res.wms);
        setExpired(false);
      }

      // 2. Sẵn sàng mới thêm lớp vào bản đồ
      setLandsatItemsLeft(prev => [...prev, { ...props.item, wmsUrl: activeWms }]);

      // 3. Phóng tới ảnh trên bản đồ
      const boundsLeft = getBoundsFromItem(props.item);
      const targetMapLeft = window.beforeMap || window.map;
      if (boundsLeft && targetMapLeft) {
        targetMapLeft.fitBounds(boundsLeft, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } catch (err: any) {
      console.error('Failed to refresh Landsat WMS:', err);
      toast.error('Không thể chuẩn bị ảnh Landsat: ' + (err?.message || 'Lỗi không xác định'), {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
      });
    } finally {
      setIsAdding(false);
      setLandsatLoading({ isLoading: false, text: '' });
    }
  };

  // Function to add image to the right map
  const onClick_AddToRightMap = async () => {
    // Check if the item already exists in the right map
    const exists = landsatItemsRight.some(item => item.id === props.item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }

    setIsAdding(true);
    setLandsatLoading({
      isLoading: true,
      text: `Đang làm mới và chuẩn bị ${getItemName()} cho bản đồ phải...`,
    });

    try {
      // 1. Luôn gọi refresh WMS để lấy token mới nhất từ GEE và cập nhật DB
      const res = await remoteRemoteImageAlert_RefreshLandsatWMS(props.item.id);
      let activeWms = res?.wms || wms;
      if (res && res.wms) {
        activeWms = res.wms;
        setWms(res.wms);
        setExpired(false);
      }

      // 2. Sẵn sàng mới thêm lớp vào bản đồ
      setLandsatItemsRight(prev => [...prev, { ...props.item, wmsUrl: activeWms }]);

      // 3. Phóng tới ảnh trên bản đồ
      const boundsRight = getBoundsFromItem(props.item);
      const targetMapRight = window.map || window.beforeMap;
      if (boundsRight && targetMapRight) {
        targetMapRight.fitBounds(boundsRight, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } catch (err: any) {
      console.error('Failed to refresh Landsat WMS:', err);
      toast.error('Không thể chuẩn bị ảnh Landsat: ' + (err?.message || 'Lỗi không xác định'), {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
      });
    } finally {
      setIsAdding(false);
      setLandsatLoading({ isLoading: false, text: '' });
    }
  };

  // Dropdown menu items
  const menuItems = [
    {
      key: 'add-right',
      label: 'Thêm vào bản đồ phải',
      icon: <PlusOutlined />,
      onClick: onClick_AddToRightMap,
    },
    {
      key: 'add-left',
      label: 'Thêm vào bản đồ trái (để so sánh)',
      icon: <PlusOutlined />,
      onClick: onClick_AddToLeftMap,
    },
    {
      key: 'qgis',
      label: 'Thêm vào QGIS',
      icon: <CloudUploadOutlined />,
      onClick: onClick_ViewOnQGIS,
    },
  ];

  const captureDate = parseDateFromId(props.item.id);
  const formattedDate = captureDate ? dayjs(captureDate).format('DD/MM/YYYY') : '';
  const shortId = props.item.id.split('/').at(-1) || props.item.id;

  return (
    <UI_Item_Wrapper>
      <UI_Header>
        <UI_TitleContainer>
          <UI_Name title={`Landsat ${shortId}`}>Landsat {shortId}</UI_Name>
          <UI_Sub>{formattedDate ? `Chụp ngày ${formattedDate}` : 'Landsat 8/9 OLI'}</UI_Sub>
        </UI_TitleContainer>
        <Dropdown 
          menu={{ items: menuItems }} 
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<EllipsisOutlined />} 
            size="small"
            disabled={isAdding}
            onClick={(e) => e.preventDefault()}
          />
        </Dropdown>
      </UI_Header>

      <UI_ImageContainer onClick={onClick_AddToRightMap} title="Nhấp để thêm vào bản đồ phải">
        <img
          src={`${baseURL}/api/remote-image-alert-ext/landsat/thumbnail?id=${props.item.id}`}
          alt={getItemName()}
          loading="lazy"
        />
        <UI_TagOverlay>
          <Tag color="#1677ff" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>Landsat</Tag>
          <Tag color="#722ed1" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>30m</Tag>
        </UI_TagOverlay>
        {isAdding && (
          <UI_AddingOverlay>
            <Spin size="large" />
            <UI_AddingText>Đang chuẩn bị ảnh...</UI_AddingText>
          </UI_AddingOverlay>
        )}
        {!isAdding && expired && (
          <UI_ExpiredOverlay>
            <UI_ExpiredText>Ảnh đã hết hạn</UI_ExpiredText>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                onClick_Refresh();
              }} 
              loading={isRefreshing}
            >
              Làm mới
            </Button>
          </UI_ExpiredOverlay>
        )}
      </UI_ImageContainer>

      <UI_Footer>
        <Button size="small" type="primary" ghost onClick={onClick_AddToLeftMap} disabled={isAdding}>
          + Trái
        </Button>
        <Button size="small" type="primary" onClick={onClick_AddToRightMap} loading={isAdding}>
          + Phải
        </Button>
      </UI_Footer>
    </UI_Item_Wrapper>
  );
});

export default LandsatItem;

const UI_Item_Wrapper = styled.div`
  width: 250px;
  height: 270px;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #1677ff;
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.12);
  }
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const UI_TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  margin-right: 6px;
`;

const UI_Name = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f1f1f;
`;

const UI_Sub = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
`;

const UI_ImageContainer = styled.div`
  position: relative;
  flex: 1;
  margin: 4px 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
`;

const UI_TagOverlay = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  gap: 4px;
`;

const UI_Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const UI_ExpiredOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 6px;
  z-index: 2;
`;

const UI_ExpiredText = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const UI_AddingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.72);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  border-radius: 6px;
  z-index: 10;
`;

const UI_AddingText = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  margin-top: 10px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
`;

